/**
 * 计时时钟的**判据**（纯函数，无 React、无 DOM、无网络）。
 *
 * ## 为什么这段逻辑必须放在 shared 里
 *
 * "已用时间"是服务端权威计时（见方案 2.3）：前端只做**推算显示**，
 * 服务端 `recompute_checkin_seconds()` 才是唯一事实来源。推算规则本身是
 * 一条**必须逐字成立的不变式**：
 *
 * > 在**同一次计时会话**里，已用时间**单调不减**。
 *
 * 违反它时的表现极其容易被误判为"服务端把时间清零了"：
 * 界面正常走到 30 秒 → 心跳响应回来 → 锚点被换成更小的值 → **表针跳回 0 重新数**。
 * 2026-10-13 的真实故障就是这个形态（心跳响应解不出来、锚点被当成 0）。
 *
 * 所以这段判据不能只活在 `useServerTimer` 的闭包里（那里的每一行都依赖 React
 * 的渲染时机，测不到）。抽成纯函数后，"收到一次刷新/轮询后 elapsed 必须不减"
 * 可以用一行断言守住，而 hook 只剩"把纯函数接到 React 上"。
 *
 * ⚠️ 与 `apps/api` 的 `recompute_checkin_seconds()` **语义对齐**：
 * 只累计 running 区间、暂停期间不计入、终态后不再增长。
 */

/**
 * 计时锚点：`serverSeconds` 是**服务端确认过的**累计秒数，
 * `monotonicMs` 是记下它那一刻的**单调时钟**读数（`performance.now()`）。
 *
 * 为什么用单调时钟而不是 `Date.now()`：后者会被"改手机系统时间"影响，
 * 孩子把时间往前调一小时就能骗到时长。`performance.now()` 不受影响。
 */
export interface TimerAnchor {
  /** 记下这个锚点时的单调时钟读数（毫秒）。 */
  monotonicMs: number;
  /** 那一刻服务端确认的累计秒数。 */
  serverSeconds: number;
  /** 界面上**已经显示过**的最大秒数（单调不减的守卫，见 `advanceTimerClock`）。 */
  displayedSeconds: number;
}

/** `{ anchor, elapsedSeconds }`：新锚点 + 该锚点在 `nowMs` 时应显示的秒数。 */
export interface TimerClockReading {
  anchor: TimerAnchor;
  elapsedSeconds: number;
}

/**
 * 初始锚点：服务端确认 0 秒，显示 0 秒。
 *
 * `monotonicMs` 必须传入调用方的当前读数 —— 不要在这里调 `performance.now()`，
 * 那会让这个模块变成"有隐式环境依赖"的函数（也就没法在 Node 里测了）。
 */
export function initialTimerAnchor(monotonicMs: number): TimerAnchor {
  return { monotonicMs, serverSeconds: 0, displayedSeconds: 0 };
}

/** 把任意输入夹成一个合法的秒数（负数 / NaN / 非有限值 → 0）。 */
function sanitizeSeconds(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * 用**服务端的权威秒数**重新锚定。
 *
 * ⚠️ 关键守则：**锚点只能前进，不能后退**。
 * 新锚点的 `displayedSeconds` 取 `max(已显示过, 服务端给的秒数)`。
 * 这样即使上游给了个偏小/为 0 的值（心跳响应解析失败、乱序到达的旧响应、
 * 服务端包了一层新字段名……），表针也**不会跳回去**，最多停在原地等下一次心跳。
 *
 * 注意：这**不是**在"掩盖 bug"。服务端的 `recompute_checkin_seconds()` 与
 * `checkin.actual_seconds` 一直是正确的（有生产数据为证），所以"不改小"
 * 不会显示一个比事实更大的数；它只防止**显示层把已经走过的秒数丢掉**。
 * 而"必须变小"的真实场景（重新开始一次计时）走的是**换会话**这条路径：
 * 调用方在 `checkinId` 变化时用 `initialTimerAnchor()` 重建锚点。
 */
export function anchorFromServerSeconds(
  previous: TimerAnchor,
  serverSeconds: number,
  nowMs: number,
): TimerAnchor {
  const safe = sanitizeSeconds(serverSeconds);
  return {
    monotonicMs: nowMs,
    serverSeconds: Math.max(previous.displayedSeconds, safe),
    displayedSeconds: Math.max(previous.displayedSeconds, safe),
  };
}

/**
 * 按单调时钟推进读数（tick / 心跳 / 服务端返回新数据都走它）。
 *
 * `observedServerSeconds` 省略 = 只推进本地时钟（每秒的显示 tick）；
 * 传了 = 先用它重新锚定，再从新锚点推进（心跳回来的那一刻）。
 */
export function advanceTimerClock(
  anchor: TimerAnchor,
  nowMs: number,
  observedServerSeconds?: number,
): TimerClockReading {
  const base =
    observedServerSeconds === undefined
      ? anchor
      : anchorFromServerSeconds(anchor, observedServerSeconds, nowMs);

  const deltaMs = nowMs - base.monotonicMs;
  const deltaSeconds = Math.floor((Number.isFinite(deltaMs) ? deltaMs : 0) / 1000);
  const elapsedSeconds = Math.max(0, base.serverSeconds + deltaSeconds);

  return {
    anchor: { ...base, displayedSeconds: elapsedSeconds },
    elapsedSeconds,
  };
}
